import {
  screenClaimResultSchema,
  screenSchema,
  screenSelfResultSchema,
  screenSiteSchema,
  type Screen,
  type ScreenSite,
  type ScreenActionRequest,
  type ScreenClaimResult,
  type ScreenRemoteKey,
  type ScreenScene,
  type ScreenStep,
} from '@secretario/shared';
import { z } from 'zod';
import { API_BASE, apiFetch } from './http.js';

/** A página da tela (sem conta): um código novo ou a mesma tela de antes. */
export async function claimScreen(label: string, token: string | null): Promise<ScreenClaimResult> {
  return screenClaimResultSchema.parse(
    await apiFetch<unknown>('/v1/screens/claim', {
      method: 'POST',
      body: { label, ...(token ? { token } : {}) },
      auth: false,
    }),
  );
}

export const screenEventsUrl = (id: string, token: string) =>
  `${API_BASE}/v1/screens/${encodeURIComponent(id)}/events?token=${encodeURIComponent(token)}`;

/** Telas conectadas são do plano Completo: false mostra o convite para o plano. */
export async function screenAccess(): Promise<boolean> {
  const result = await apiFetch<{ allowed?: boolean }>('/v1/screens/access');
  return result.allowed !== false;
}

export async function listScreens(): Promise<Screen[]> {
  const result = await apiFetch<{ screens: unknown }>('/v1/screens');
  return z.array(screenSchema).parse(result.screens);
}

export async function pairScreen(code: string, name?: string): Promise<Screen> {
  return screenSchema.parse(
    await apiFetch<unknown>('/v1/screens/pair', {
      method: 'POST',
      body: { code, ...(name ? { name } : {}) },
    }),
  );
}

/** Tela aberta pelo próprio celular (Smart View): já pareada, com o segredo para a página. */
export async function createSelfScreen(name: string): Promise<{ screen: Screen; token: string }> {
  return screenSelfResultSchema.parse(
    await apiFetch<unknown>('/v1/screens/self', { method: 'POST', body: { name } }),
  );
}

export const renameScreen = (id: string, name: string) =>
  apiFetch<void>(`/v1/screens/${encodeURIComponent(id)}`, { method: 'PATCH', body: { name } });

export const removeScreen = (id: string) =>
  apiFetch<void>(`/v1/screens/${encodeURIComponent(id)}`, { method: 'DELETE' });

/** Passa a mostrar nesta tela (com uma cena, se houver). */
export async function useScreen(id: string, scene?: ScreenScene): Promise<Screen> {
  return screenSchema.parse(
    await apiFetch<unknown>(`/v1/screens/${encodeURIComponent(id)}/show`, {
      method: 'POST',
      body: scene ? { scene } : {},
    }),
  );
}

export const releaseScreen = () => apiFetch<void>('/v1/screens/active', { method: 'DELETE' });

/** O celular conta à tela ativa o que o Secretário está fazendo e dizendo. */
export const postScreenSteps = (steps: ScreenStep[]) =>
  apiFetch<void>('/v1/screens/active/steps', { method: 'POST', body: { steps } });
export const postScreenSay = (text: string) =>
  apiFetch<void>('/v1/screens/active/say', { method: 'POST', body: { text } });

/** Ação do controle da tela (remoto, mouse, passador): sem modelo, sem pedido do plano. */
export async function screenAction(
  id: string,
  body: ScreenActionRequest,
): Promise<{ ok: boolean; mensagem?: string }> {
  return apiFetch<{ ok: boolean; mensagem?: string }>(
    `/v1/screens/${encodeURIComponent(id)}/action`,
    { method: 'POST', body, auth: false },
  );
}

/** Endereço de um arquivo da tela pelo vale (imagem gerada, foto, PDF). */
export const screenAssetUrl = (id: string, key: string, token: string) =>
  `${API_BASE}/v1/screens/${encodeURIComponent(id)}/assets/${encodeURIComponent(key)}?token=${encodeURIComponent(token)}`;

/**
 * Moldura de um site liberado: o servidor recebe só o domínio; o endereço completo vai
 * depois do "#" (não sai do navegador da tela).
 */
export const screenWebUrl = (id: string, token: string, site: string, endereco: string) =>
  `${API_BASE}/v1/screens/${encodeURIComponent(id)}/web?token=${encodeURIComponent(token)}&site=${encodeURIComponent(site)}#${encodeURIComponent(endereco)}`;

/** O que a tela grande montou e ficou guardado (Documentos › Da tela grande). */
export type ScreenItem = {
  id: string;
  kind: string;
  title: string;
  saved: boolean;
  createdAt: string;
  /** A cena como na TV; as imagens vêm embutidas (data:). */
  scene: ScreenScene;
};

export const screenItemKey = (id: string) => ['screen-item', id] as const;

export async function getScreenItem(id: string): Promise<ScreenItem> {
  return apiFetch<ScreenItem>(`/v1/screen-items/${encodeURIComponent(id)}`);
}

export async function saveScreenItem(id: string): Promise<void> {
  await apiFetch(`/v1/screen-items/${encodeURIComponent(id)}/save`, { method: 'POST', body: {} });
}

/** Manda para a tela grande aberta agora (sem gastar IA). */
export async function showScreenItem(id: string): Promise<{ ok: boolean; mensagem?: string }> {
  return apiFetch<{ ok: boolean; mensagem?: string }>(
    `/v1/screen-items/${encodeURIComponent(id)}/show`,
    { method: 'POST', body: {} },
  );
}

/** Tecla do controle no celular para a tela em exibição (sem IA). */
export async function screenKey(key: ScreenRemoteKey): Promise<boolean> {
  const result = await apiFetch<{ ok: boolean }>('/v1/screens/active/key', {
    method: 'POST',
    body: { key },
  });
  return result.ok;
}

/** Deslizar no controle do celular: quanto o ponteiro da tela anda (fração da tela). */
export async function screenPointer(dx: number, dy: number): Promise<boolean> {
  const result = await apiFetch<{ ok: boolean }>('/v1/screens/active/pointer', {
    method: 'POST',
    body: { dx, dy },
  });
  return result.ok;
}

/** Sites que abrem na tela: os da lista do serviço e os que a pessoa liberou. */
export async function listScreenSites(): Promise<ScreenSite[]> {
  const result = await apiFetch<{ sites: unknown }>('/v1/screens/sites');
  return z.array(screenSiteSchema).parse(result.sites);
}

export async function addScreenSite(site: string): Promise<ScreenSite> {
  return screenSiteSchema.parse(
    await apiFetch<unknown>('/v1/screens/sites', { method: 'POST', body: { site } }),
  );
}

export async function removeScreenSite(id: string): Promise<void> {
  await apiFetch<unknown>(`/v1/screens/sites/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
